import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchSettings = createAsyncThunk(
  "settings/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/settings");
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch settings"
      );
    }
  }
);

export const updateSettings = createAsyncThunk(
  "settings/updateSettings",
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await api.put("/admin/settings", {
        settings: settingsData,
      });
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update settings"
      );
    }
  }
);

export const updateSetting = createAsyncThunk(
  "settings/updateSetting",
  async ({ key, value, type }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/settings/${key}`, { value, type });
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update setting"
      );
    }
  }
);

export const resetSetting = createAsyncThunk(
  "settings/resetSetting",
  async (key, { rejectWithValue }) => {
    try {
      await api.post(`/admin/settings/${key}/reset`);
      return { key };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset setting"
      );
    }
  }
);

export const fetchPublicSettings = createAsyncThunk(
  "settings/fetchPublicSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/settings/public");
      return response.data?.data || {};
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch public settings"
      );
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    data: {},
    publicSettings: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateLocalSetting: (state, action) => {
      const { category, key, value } = action.payload;
      if (state.data[category]) {
        const settingIndex = state.data[category].findIndex(
          (s) => s.key === key
        );
        if (settingIndex !== -1) {
          state.data[category][settingIndex].value = value;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Settings
      .addCase(updateSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.loading = false;
        // The settings will be refetched after update
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Single Setting
      .addCase(updateSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific setting in local state
        Object.keys(state.data).forEach((category) => {
          const settingIndex = state.data[category].findIndex(
            (s) => s.key === action.payload.key
          );
          if (settingIndex !== -1) {
            state.data[category][settingIndex] = {
              ...state.data[category][settingIndex],
              ...action.payload,
            };
          }
        });
      })
      .addCase(updateSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Setting
      .addCase(resetSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetSetting.fulfilled, (state, action) => {
        state.loading = false;
        // The settings will be refetched after reset
      })
      .addCase(resetSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Public Settings
      .addCase(fetchPublicSettings.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchPublicSettings.fulfilled, (state, action) => {
        state.publicSettings = action.payload;
      })
      .addCase(fetchPublicSettings.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, updateLocalSetting } = settingsSlice.actions;
export default settingsSlice.reducer;
